// @ts-strict-ignore
import stringify from 'json-stable-stringify';

import arbselectorDefs from 'common/util/tern/definitions/arb-selector.json';
import {PropertySectionItem} from 'common/query-builder/query-block/properties-menu/item-menu-types';
import {eventPropertyValuesClient} from 'common/report/queries/query-api/event-property-values-client';
import {peoplePropertyValuesClient} from 'common/report/queries/query-api/people-property-values-client';
import {truncateMiddle} from 'common/util/string';
import {isProperty} from 'common/query-builder/query-block/properties-menu/types';
import {CustomProperty} from 'common/types/reports/query';
import {PropertyType} from 'common/report/constants';

interface FetchedPropertyValues {
  status: string;
  value?: string[];
  reason?: any;
}

interface ComposedPropertyValues {
  [variable: string]: Promise<FetchedPropertyValues>;
}

export class CustomPropertyCompleter {
  declare composedPropertyValues: ComposedPropertyValues;

  async fetchPropertyValues(projectId: number, item: PropertySectionItem): Promise<string[]> {
    const {value, resourceType} = item;
    let customProperty: CustomProperty, propertyDefaultType: PropertyType, dataGroupId: string;
    if (isProperty(item)) {
      ({customProperty, propertyDefaultType, dataGroupId} = item);
    }
    if (propertyDefaultType === `string`) {
      if (resourceType === `event`) {
        return eventPropertyValuesClient.query({
          projectId,
          ...(customProperty ? {customProperty: {customProperty}} : {propertyName: value}),
        });
      } else if (resourceType === `user`) {
        return peoplePropertyValuesClient.query({
          projectId,
          propertyName: customProperty ? {customProperty} : value,
          ...(dataGroupId ? {dataGroupId} : {}),
        });
      }
    }
    return Promise.resolve([]);
  }

  setComposedProperties(projectId: number, composedProperties: Record<string, TODO>) {
    const composedPropertyValues: ComposedPropertyValues = {};
    for (const property of Object.values(composedProperties || {})) {
      // make sure the promise never fails, so we can use it with Promise.all without short-circuiting
      composedPropertyValues[stringify(property)] = this.fetchPropertyValues(projectId, property)
        .then((value) => ({
          status: `fulfilled`,
          value,
        }))
        .catch((reason) => ({
          status: `rejected`,
          reason,
        }));
    }
    this.composedPropertyValues = composedPropertyValues;
  }

  // Don't change this method signature; this is called by the ace editor
  getCompletions(_editor, session, position, _prefix, callback) {
    try {
      const currToken = session.getTokenAt(position.row, position.column);
      const currTokenType = currToken ? currToken.type : `text`;
      if (currTokenType === `string.quoted`) {
        const tokens = session.getTokens(position.row);
        for (const token of tokens.entries()) {
          token[1].index = token[0];
        }
        const variables = Object.entries(
          tokens
            .filter((t) => t.type === `encoded_property`)
            .map((t, i) => Object.assign(t, {score: 1000 - Math.abs(i - currToken.index)}))
            .reduce((r, t) => {
              r[t.value] = Math.max(r[t.value] || 0, t.score);
              return r;
            }, {}),
        ).map((x) => ({property: JSON.parse(x[0].substring(4, x[0].length - 4)), score: x[1]}));
        Promise.all(variables.map((v) => this.composedPropertyValues[stringify(v.property)])).then((promises) => {
          const fullfilledVariables = variables
            .map((variable, i) => Object.assign(variable, {promise: promises[i]}))
            .filter((data) => data.promise.status === `fulfilled`);
          const completions = fullfilledVariables.flatMap((data) =>
            data.promise.value.map((v) => ({
              caption: v,
              value: v,
              meta: truncateMiddle(data.property.label, 14),
              score: data.score,
            })),
          );
          callback(null, completions);
        });
      } else if (currTokenType !== `comment`) {
        const completions = Object.keys(arbselectorDefs)
          .filter((key) => !key.startsWith(`!`))
          .map((key) => ({
            caption: key,
            value: key.includes(`(`) ? key.split(`(`)[0] + `(` : key,
            tooltip: {
              header: key,
              subheader: arbselectorDefs[key][`syntax`],
              description: arbselectorDefs[key][`!doc`],
              details: [
                {
                  icon: `info`,
                  name: `Example`,
                  value: arbselectorDefs[key][`examples`],
                },
              ],
            },
          }));
        callback(null, completions);
      }
    } catch (err) {
      callback(err, null);
    }
  }
}
